import { Moon, Sun } from 'lucide-react';
import { Button, cn, useTheme } from 'shared-ui';
import { useMediaQuery } from 'usehooks-ts';

import orbitLogoFullBlack from '@/assets/orbit-logo-full-black.png';
import orbitLogoFullWhite from '@/assets/orbit-logo-full-white.png';
import { useAuth } from '@/auth/useAuth';

const CURRENT_YEAR = new Date().getFullYear();

export function SignInPage() {
    const auth = useAuth();
    const { resolvedMode, setMode } = useTheme();
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    return (
        <div className="grid h-screen md:grid-cols-2">
            <div className="bg-background flex flex-col overflow-y-auto px-8 pt-6 md:px-12">
                <div className="flex flex-1 flex-col items-center justify-center py-8">
                    <img
                        src={resolvedMode === 'dark' ? orbitLogoFullWhite : orbitLogoFullBlack}
                        alt="Origin Orbit"
                        className="mb-16 h-[72px] w-auto"
                    />

                    <h1 className="text-foreground mb-10 text-center text-4xl leading-tight font-black tracking-normal uppercase md:text-6xl md:leading-[1.1]">
                        <span className="block whitespace-nowrap">Welcome to</span>{' '}
                        <span className="block whitespace-nowrap">
                            Origin Orbit
                            <sup aria-hidden="true" className="ml-0.5 text-[0.6em]">
                                ®
                            </sup>
                        </span>
                    </h1>

                    <div className="flex w-full max-w-[320px] flex-col items-center gap-4 text-center">
                        <p className="text-foreground text-sm">To get started, please sign in below</p>

                        <Button
                            variant="default"
                            onClick={() => {
                                auth.signIn();
                            }}
                            disabled={auth.isLoading}
                            className="h-10 w-full rounded-md font-semibold"
                            aria-label="Sign in with Microsoft"
                        >
                            {auth.isLoading ? 'Signing in…' : 'Sign In'}
                        </Button>

                        <p className="text-muted-foreground text-xs">
                            By using this site, you agree to the
                            <br />
                            EULA and{' '}
                            <a
                                href="/origin-eula.pdf"
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary focus-visible:ring-ring underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:outline-none"
                            >
                                Terms &amp; Conditions
                            </a>
                            .
                        </p>
                    </div>
                </div>

                <div className="flex justify-center pb-10">
                    <div
                        className="border-border inline-flex w-fit items-center gap-1 overflow-hidden rounded-md border p-0.5"
                        style={{
                            background:
                                resolvedMode === 'light'
                                    ? 'linear-gradient(to right, var(--background) 50%, var(--secondary) 50%)'
                                    : 'linear-gradient(to right, var(--accent) 50%, var(--background) 50%)',
                        }}
                        role="group"
                        aria-label="Colour theme"
                    >
                        <button
                            type="button"
                            onClick={() => {
                                setMode('dark');
                            }}
                            aria-label="Switch to dark mode"
                            aria-pressed={resolvedMode === 'dark'}
                            className={cn(
                                'focus-visible:ring-ring flex size-10 cursor-pointer items-center justify-center rounded-[5px] transition-colors focus-visible:ring-1 focus-visible:outline-none',
                                resolvedMode === 'dark'
                                    ? 'bg-accent text-primary'
                                    : 'text-foreground hover:text-foreground',
                            )}
                        >
                            <Moon className="size-4" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setMode('light');
                            }}
                            aria-label="Switch to light mode"
                            aria-pressed={resolvedMode === 'light'}
                            className={cn(
                                'focus-visible:ring-ring flex size-10 cursor-pointer items-center justify-center rounded-[5px] transition-colors focus-visible:ring-1 focus-visible:outline-none',
                                resolvedMode === 'light'
                                    ? 'bg-secondary text-primary'
                                    : 'bg-secondary text-foreground hover:text-foreground',
                            )}
                        >
                            <Sun className="size-4" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <div className="border-border -mx-8 flex h-[100px] items-center justify-center border-t md:-mx-12">
                    <footer className="text-muted-foreground text-center text-xs">
                        Copyright {CURRENT_YEAR}. All rights reserved.
                    </footer>
                </div>
            </div>

            {/* ── Right panel: video background (desktop only) ──────────── */}
            <div
                className="relative overflow-hidden max-md:hidden"
                style={{
                    backgroundImage: 'url(/initial-frame.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
                aria-hidden="true"
            >
                {!prefersReducedMotion ? (
                    <video className="absolute inset-0 h-full w-full object-cover" autoPlay muted loop playsInline>
                        <source src="/origin-video.webm" type="video/webm" />
                    </video>
                ) : (
                    // Static fallback when the user prefers reduced motion
                    <img src="/initial-frame.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
                )}
            </div>
        </div>
    );
}
